"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LibraryConfig = void 0;
const fs_1 = __importDefault(require("fs"));
const object_hash_1 = __importDefault(require("object-hash"));
const os_1 = __importDefault(require("os"));
const util_1 = __importDefault(require("util"));
const types_1 = require("@refinitiv-data/types");
const detect_environment_1 = require("../util/detect-environment");
const base_config_1 = require("./base-config");
const config_util_1 = require("./config-util");
/**
 * This class provides all the functionality to work with configuration files
 * within the project (TS library)
 *
 * Configuration levels:
 * 1. Default - hardcoded values in the config-schema.ts file.
 * (to make it possible come back to default configuration after config files changing and re-read them).
 * Can be overridden by the higher levels (2, 3 ...)
 * 2. User's Home directory level. For Unix - $HOME, Windows - %HOMEPATH%. Can be overridden by the higher levels (3, ...)
 * 3. Project Working Directory level - process.cwd(). Can be overridden by higher levels (env, args)
 * 4. Environment variables can't be overridden by config files, but can with the process arguments (node index.js --loglevel value)
 * 5. Process arguments can't be overridden. But you still can manipulate them programmatically in your code.
 * How to set up env variables and process arguments see 'convict npm => readme.md'
 * Default config is saved to config-schema.ts
 */
class LibraryConfig extends base_config_1.BaseConfig {
    get isBrowser() {
        return (0, detect_environment_1.detectEnvironment)() === types_1.Environment.WEB;
    }
    constructor(schema) {
        super(schema);
        this.configFileChangedHandler = this.configFileChangedHandler.bind(this);
        this.watchedFiles = [];
        this.init();
    }
    /**
     * Returns default event name. Is used as event name to notify that config
     * has been changed
     */
    get CONFIG_FILE_UPDATED() {
        return types_1.CONFIG_FILE_UPDATED;
    }
    /**
     * Event name for event when method set() is used to change a config
     */
    get CONFIG_SET() {
        return types_1.NEW_CONFIG_VALUE_SET;
    }
    set(name, value) {
        const setResult = super.set(name, value);
        this.emit(this.CONFIG_SET, this.get(), name, value);
        return setResult;
    }
    /**
     * Returns path of configuration files to be monitored for changes
     * If the file does not exist - the path to the file will still be present in this list
     * The file with the higher index in the array has higher priority
     */
    getConfigFilesPath() {
        const env = this.get('platform');
        const configDir = this.get('dir');
        const userConfigFilePath = (0, config_util_1.getConfigFilePath)(env, os_1.default.homedir());
        const projectConfigFilePath = (0, config_util_1.getConfigFilePath)(env, configDir);
        return Array.from(new Set([userConfigFilePath, projectConfigFilePath]));
    }
    /**
     * EventEmitter event to notify observes that any config file has been changed
     * @param {string} event Name of event to be emitted once any config file has been changed. Default - 'update'.
     * @param {ConfigSchema} data Configuration object. Default - full configuration object after merging all the config files.
     * @param {string} source Source of the configuration which has been changed (file path of 'default-config')
     */
    notifyConfigUpdated(event = types_1.CONFIG_FILE_UPDATED, data = this.get(), source) {
        return this.emit(event, data, source);
    }
    /**
     * Watch for changes in the files and re-read config files on any changes (delete/add/change a file)
     */
    watch() {
        if (this.isBrowser) {
            return;
        }
        this.getConfigFilesPath().forEach(file => {
            if (this.watchedFiles.includes(file)) {
                return;
            }
            fs_1.default.watchFile(file, (curr, prev) => this.configFileChangedHandler(curr, prev, file));
            this.watchedFiles.push(file);
        });
    }
    /**
     * Remove configuration file watchers
     */
    unwatch() {
        if (this.isBrowser) {
            return;
        }
        this.watchedFiles.forEach(file => fs_1.default.unwatchFile(file));
        this.watchedFiles = [];
    }
    /**
     * Method to be used by developers to extend/override default library config to new defaults
     * @param configExtend Object to extend default configuration
     */
    extendDefault(configExtend) {
        this.registerExtendedDefault(configExtend);
        this.resetConfig();
        this.init();
    }
    /**
     * Read and merge all the configuration files with appropriate priorities
     */
    init() {
        if (this.isBrowser) {
            return;
        }
        const configFileList = this.getConfigFilesPath();
        for (const configFile of configFileList) {
            try {
                const fileContent = fs_1.default.readFileSync(configFile, { encoding: 'utf-8' });
                const withReplacedTemplates = (0, config_util_1.replaceConfigTemplates)(fileContent);
                this.load(withReplacedTemplates);
                // tslint:disable-next-line: no-empty
            }
            catch (error) { }
        }
    }
    /**
     * Default watcher for changes in config files
     * @param {Stats} current Current Stats of a watched file
     * @param {Stats} prev Previous Stats of a watched file
     * @param {string} source Source of the configuration which has been changed (file path of 'default-config')
     */
    async configFileChangedHandler(current, prev, source) {
        if (current.isFile() || prev.isFile()) {
            const previousConfig = this.get();
            this.resetConfig();
            for (const file of this.getConfigFilesPath()) {
                try {
                    const fileContent = await util_1.default.promisify(fs_1.default.readFile)(file, { encoding: 'utf-8' });
                    const replacedTemplates = (0, config_util_1.replaceConfigTemplates)(fileContent);
                    this.load(replacedTemplates);
                    // tslint:disable-next-line: no-empty
                }
                catch (error) { }
            }
            const currentConfig = this.get();
            // @ts-ignore
            if ((0, object_hash_1.default)(previousConfig) !== (0, object_hash_1.default)(currentConfig)) {
                this.notifyConfigUpdated(undefined, undefined, source);
            }
        }
    }
}
exports.LibraryConfig = LibraryConfig;
//# sourceMappingURL=data:application/json;base64,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